/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { Modal } from '../../shared/modals/Modal';
import { FlatList, Pressable, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import type { TransferRow } from '../../inventory/stock/public-api';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader'
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { formatStockQuantity } from '../application/inventoryUtils';

type BranchLike = { id: string; name: string };

type Props = {
  visible: boolean;
  pendingTransfers: TransferRow[];
  branches: BranchLike[];
  canEdit: boolean;
  processingTransferId: string | null;
  onClose: () => void;
  onReceive: (transfer: TransferRow) => void;
  onReject: (transfer: TransferRow) => void;
};

const NARROW_MAX_WIDTH = 400;
const LIST_HEIGHT = 340;

const formatWhen = (iso: string) => formatLocalDateTime(iso);

export const PendingTransfersModal = ({
  visible,
  pendingTransfers,
  branches,
  canEdit,
  processingTransferId,
  onClose,
  onReceive,
  onReject,
}: Props) => {
  const modalBackdropPadding = useModalBackdropPadding();
  const { width } = useWindowDimensions();
  const narrow = width < NARROW_MAX_WIDTH;
  const isMobile = width < 768;
  const insets = useSafeAreaInsets();
  const { confirm: confirmTransferAction, ConfirmModal: TransferActionConfirm } =
    useOperationalConfirm();

  const branchName = (id: string) =>
    branches.find((b) => b.id === id)?.name ?? 'la sucursal de origen';

  // Recibir suma stock y completa la transferencia — acción irreversible.
  const handleReceive = (item: TransferRow) => {
    void confirmTransferAction({
      title: 'Recibir transferencia',
      message: `Se suman ${formatStockQuantity(item.quantity, item.unitOfMeasure)} de ${item.productName ?? 'este producto'} al stock de esta sucursal.`,
      confirmLabel: 'Recibir',
      iconName: 'checkmark-circle-outline',
      presentation: 'overlay',
    }).then((confirmed) => {
      if (confirmed) onReceive(item);
    });
  };

  // Rechazar cancela el envío y devuelve el stock al origen — no hay
  // reenvío: la sucursal de origen tiene que transferir de nuevo.
  const handleReject = (item: TransferRow) => {
    void confirmTransferAction({
      title: 'Rechazar transferencia',
      message: `Se cancela el envío de ${formatStockQuantity(item.quantity, item.unitOfMeasure)} de ${item.productName ?? 'este producto'}. El stock vuelve a ${branchName(item.fromBranchId)}.`,
      confirmLabel: 'Rechazar',
      destructive: true,
      iconName: 'close-circle-outline',
      presentation: 'overlay',
    }).then((confirmed) => {
      if (confirmed) onReject(item);
    });
  };

  return (
    <Modal visible={visible} transparent animationType={isMobile ? 'slide' : 'fade'} onRequestClose={onClose}>
        <Pressable style={[posUi.modalBackdrop, isMobile ? { justifyContent: 'flex-end' } : modalExperience.modalRoot, isMobile ? null : modalBackdropPadding]} onPress={isMobile ? onClose : undefined}>
          <Pressable
            style={[isMobile ? [styles.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm) }] : [posUi.modalCard, modalShellShared.modalCardShell, modalExperience.modalWidthStandard]]}
            onPress={(e) => e.stopPropagation()}
          >
          {isMobile ? <View style={styles.grabber} /> : null}
          <TicketModalHeader
            kicker="Stock · Recepción"
            title="Transferencias pendientes"
            subtitle={`${pendingTransfers.length} pendientes · Recibí o rechazá cada envío. El stock se suma al recibir.`}
            onClose={onClose}
            compact={isMobile}
          />

          {pendingTransfers.length === 0 ? (
            <ModalStatePanel
              empty
              emptyTitle="Sin pendientes"
              emptyMessage="No hay transferencias para recibir en esta sucursal."
            />
          ) : (
            <View style={[modalExperience.listContainer, styles.listWrap]}>
              <FlatList
                data={pendingTransfers}
                keyExtractor={(item) => item.id}
                style={styles.flatList}
                renderItem={({ item }) => {
                  const fromName = branches.find((b) => b.id === item.fromBranchId)?.name ?? 'Sucursal origen';
                  const isProcessing = processingTransferId === item.id;
                  const actionsDisabled = !canEdit || processingTransferId != null;
                  const variantLabel = item.variantId ? (item.variantLabel ?? 'Variante') : null;
                  return (
                    <View style={[styles.row, narrow && styles.rowNarrow]}>
                      <View style={styles.rowMain}>
                        <Text style={modalExperience.listRowTitle} numberOfLines={2}>
                          {item.productName ?? item.productId}
                        </Text>
                        {variantLabel ? (
                          <View style={styles.variantChip}>
                            <Text style={styles.variantChipText} numberOfLines={1}>
                              {variantLabel}
                            </Text>
                          </View>
                        ) : null}
                        <Text style={modalExperience.listMeta} numberOfLines={1}>
                          {formatStockQuantity(item.quantity, item.unitOfMeasure)} · Desde {fromName}
                        </Text>
                        <Text style={modalExperience.listMetaMuted} numberOfLines={1}>
                          {formatWhen(item.performedAt)}
                        </Text>
                      </View>
                      <View style={[styles.actions, narrow && styles.actionsNarrow]}>
                        <TouchableOpacity
                          style={[
                            styles.actionBtn,
                            styles.actionReceive,
                            narrow && styles.actionBtnFlex,
                            actionsDisabled && styles.actionBtnDisabled,
                          ]}
                          onPress={() => handleReceive(item)}
                          disabled={actionsDisabled}
                          testID={`pendingTransfers.receive.${item.id}`}
                          accessibilityRole="button"
                          accessibilityLabel={`Recibir ${item.productName ?? ''}`}
                        >
                          <Text style={[posUi.secondaryButtonText, styles.actionReceiveText]}>
                            {isProcessing ? 'Procesando…' : 'Recibir'}
                          </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[
                            styles.actionBtn,
                            styles.actionReject,
                            narrow && styles.actionBtnFlex,
                            actionsDisabled && styles.actionBtnDisabled,
                          ]}
                          onPress={() => handleReject(item)}
                          disabled={actionsDisabled}
                          testID={`pendingTransfers.reject.${item.id}`}
                          accessibilityRole="button"
                          accessibilityLabel={`Rechazar ${item.productName ?? ''}`}
                        >
                          <Text style={[posUi.secondaryButtonText, styles.actionRejectText]}>
                            {isProcessing ? 'Procesando…' : 'Rechazar'}
                          </Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  );
                }}
              />
            </View>
          )}
          </Pressable>
          {/* Confirmaciones (overlay dentro del Modal, encima de la lista). */}
          {TransferActionConfirm}
        </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  sheetCard: {
    width: '100%',
    maxHeight: '92%',
    backgroundColor: ticketColors.paper2,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingTop: 8,
  },
  grabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: ticketColors.rule,
    alignSelf: 'center',
    marginBottom: spacing.xs,
  },
  listWrap: {
    height: LIST_HEIGHT,
  },
  flatList: {
    flex: 1,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  rowNarrow: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  variantChip: {
    alignSelf: 'flex-start',
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: 6,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    marginTop: 2,
  },
  variantChipText: {
    fontSize: 11,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flexShrink: 0,
  },
  actionsNarrow: {
    alignSelf: 'stretch',
    justifyContent: 'space-between',
    marginTop: spacing.xs,
  },
  actionBtn: {
    minHeight: 44,
    minWidth: 100,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 14,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ticketColors.paper2,
  },
  actionBtnFlex: {
    flex: 1,
    minWidth: 0,
  },
  actionReceive: {
    borderColor: ticketColors.ok,
  },
  actionReceiveText: {
    color: ticketColors.ok,
    fontWeight: '700',
  },
  actionReject: {
    borderColor: ticketColors.bad,
  },
  actionRejectText: {
    color: ticketColors.bad,
    fontWeight: '700',
  },
  actionBtnDisabled: {
    opacity: 0.45,
  },
});
